import type { WorkerEngineRow, WorkerRuntimeUpgradeInfo, WorkerRuntimeUpgradeStatus } from '../types'
import { platformColors } from './theme'

export type TranslateFn = (key: string, opts?: Record<string, unknown>) => string

export type WorkerEngineReadiness =
  | 'awaiting_report'
  | 'waiting_for_activation'
  | 'activation_requested'
  | 'engine_starting'
  | 'ready'
  | 'draining'
  | 'error'
  | 'unknown'

type EngineActualStateKnown = Exclude<WorkerEngineRow['actual_state'], null>

const ENGINE_ACTUAL_STATE_KEYS: Record<EngineActualStateKnown, { key: string; color: string; dot: string; processing?: boolean }> = {
  OFFLINE: { key: 'workers.engine.state.unloaded', color: platformColors.mutedText, dot: '○' },
  STARTING: { key: 'workers.engine.state.loading', color: platformColors.info, dot: '◐', processing: true },
  WARMING: { key: 'workers.engine.state.loading', color: platformColors.info, dot: '◐', processing: true },
  READY: { key: 'workers.engine.state.ready', color: platformColors.success, dot: '●' },
  BUSY: { key: 'workers.engine.state.busy', color: platformColors.success, dot: '●' },
  DRAINING: { key: 'workers.engine.state.unloading', color: platformColors.warning, dot: '◑', processing: true },
  ERROR: { key: 'workers.engine.state.error', color: platformColors.danger, dot: '●' },
}

const READINESS_KEYS: Record<WorkerEngineReadiness, string> = {
  awaiting_report: 'workers.engine.readiness.awaitingReport',
  waiting_for_activation: 'workers.engine.readiness.waitingForActivation',
  activation_requested: 'workers.engine.readiness.activationRequested',
  engine_starting: 'workers.engine.readiness.engineStarting',
  ready: 'workers.engine.readiness.ready',
  draining: 'workers.engine.readiness.draining',
  error: 'workers.engine.readiness.error',
  unknown: 'workers.engine.readiness.unknown',
}

export function engineActualStateTone(actualState: WorkerEngineRow['actual_state'], t: TranslateFn) {
  if (!actualState) return null
  const meta = ENGINE_ACTUAL_STATE_KEYS[actualState] ?? ENGINE_ACTUAL_STATE_KEYS.OFFLINE
  return { ...meta, label: t(meta.key) }
}

export function engineReadinessLabel(readiness: WorkerEngineReadiness | undefined, t: TranslateFn): string {
  if (!readiness) return '—'
  const key = READINESS_KEYS[readiness]
  return key ? t(key) : readiness
}

export function engineReadinessIsError(readiness: WorkerEngineReadiness | undefined): boolean {
  return readiness === 'error'
}

export function engineReadinessIsIdle(readiness: WorkerEngineReadiness | undefined): boolean {
  return readiness === 'waiting_for_activation'
}

export function desiredStateLabel(desired: WorkerEngineRow['desired_state'], t: TranslateFn): string {
  if (desired == null) return t('workers.engine.desired.awaitingReport')
  return desired === 'READY' ? t('workers.engine.desired.ready') : t('workers.engine.desired.onDemand')
}

export function primaryEngine(summary: { applicable?: boolean; engines?: WorkerEngineRow[] } | undefined): WorkerEngineRow | null {
  if (!summary?.applicable || !summary.engines?.length) return null
  return summary.engines[0] ?? null
}

export function formatEngineControlLabel(engine: WorkerEngineRow, wantsOffline: boolean, t: TranslateFn): string {
  if (wantsOffline) {
    if (engine.actual_state === 'STARTING' || engine.actual_state === 'WARMING') return t('workers.engine.control.starting')
    return t('workers.engine.control.load')
  }
  if (engine.actual_state === 'DRAINING') return t('workers.engine.control.stopping')
  return t('workers.engine.control.unload')
}

export function engineControlDisabled(engine: WorkerEngineRow, wantsOffline: boolean): boolean {
  const awaitingReport = !engine.known || engine.actual_state == null
  const notInstalled = engine.installed === false
  if (awaitingReport || notInstalled) return true
  if (wantsOffline && (engine.actual_state === 'STARTING' || engine.actual_state === 'WARMING')) return true
  if (!wantsOffline && engine.actual_state === 'DRAINING') return true
  return false
}

export function engineControlLoading(engine: WorkerEngineRow, wantsOffline: boolean, actioning: boolean): boolean {
  if (!actioning) return false
  if (wantsOffline && (engine.actual_state === 'STARTING' || engine.actual_state === 'WARMING')) return true
  if (!wantsOffline && engine.actual_state === 'DRAINING') return true
  return actioning
}

export function summarizeActiveEngine(summary: { applicable?: boolean; active_engine_id?: string | null } | undefined): string {
  if (!summary?.applicable) return '—'
  if (summary.active_engine_id) return summary.active_engine_id.toUpperCase()
  return 'None'
}

export function runtimeUpgradeBadgeMeta(status: WorkerRuntimeUpgradeStatus | undefined, t: TranslateFn) {
  switch (status) {
    case 'update_required':
      return { label: t('workers.runtime.upgradeRequired'), color: 'error' as const }
    case 'update_available':
      return { label: t('workers.runtime.updateAvailable'), color: 'warning' as const }
    case 'up_to_date':
      return { label: t('workers.runtime.upToDate'), color: 'success' as const }
    default:
      return { label: t('workers.runtime.unknown'), color: 'default' as const }
  }
}

export function formatRuntimeUpgradeDetail(info: WorkerRuntimeUpgradeInfo | null | undefined, t: TranslateFn): string | null {
  if (!info?.reason) return null
  const parts = [
    info.current_runtime_version ? `${t('workers.runtime.current')}: v${info.current_runtime_version}` : null,
    info.minimum_runtime_version ? `${t('workers.runtime.minimum')}: v${info.minimum_runtime_version}` : null,
    info.recommended_runtime_version ? `${t('workers.runtime.recommended')}: v${info.recommended_runtime_version}` : null,
    info.reason,
  ].filter(Boolean)
  return parts.join(' · ')
}

export function inferGpuEngineLabel(workerId: string, summary: { applicable?: boolean; engines?: WorkerEngineRow[] } | undefined): string {
  if (summary?.engines?.length === 1) return summary.engines[0].engine_id.toUpperCase()
  if (workerId.includes('ltx')) return 'LTX'
  if (workerId.includes('minimax')) return 'MINIMAX'
  if (!summary?.applicable) return '—'
  if (!summary.engines?.length) return '—'
  return summary.engines.map((e) => e.engine_id.toUpperCase()).join(', ')
}
